import React, { useMemo } from "react";
import { useParams, Link } from "@/i18n/router";
import { ArrowLeft, ArrowRight, CalendarDays } from "lucide-react";
import { Seo } from "@/lib/seo";
import { DocContent, slugify } from "@/components/docs/DocContent";
import { DocsSidebar } from "@/components/docs/DocsSidebar";
import { DOCS, DOC_NAV } from "@/data/docs";
import { useLang } from "@/i18n/LanguageContext";
import { deepLocalize, translateString } from "@/i18n/translate";
import NotFound from "@/pages/NotFound";

// Flat ordered list of slugs for prev/next.
const ORDER = DOC_NAV.flatMap((g) => g.items);

export default function DocPage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const doc = deepLocalize(DOCS[slug], lang);

  const { toc, howTo } = useMemo(() => {
    if (!doc) return { toc: [], howTo: null };
    const toc = (doc.blocks || []).filter((b) => b.type === "h").map((b) => ({ id: slugify(b.text), text: b.text }));
    const stepsBlock = (doc.blocks || []).find((b) => b.type === "steps");
    const howTo = stepsBlock
      ? { name: doc.title, description: doc.description, steps: stepsBlock.items.map((it) => ({ name: it.title, text: it.text })) }
      : null;
    return { toc, howTo };
  }, [doc]);

  if (!doc) return <NotFound />;

  const idx = ORDER.indexOf(slug);
  const prev = idx > 0 ? ORDER[idx - 1] : null;
  const next = idx < ORDER.length - 1 ? ORDER[idx + 1] : null;

  return (
    <>
      <Seo
        title={`${doc.title} — ${translateString("Documentation", lang)}`}
        description={doc.description}
        path={`/documentation/${slug}`}
        keywords={`${doc.title}, AI governance documentation, ${doc.category}, AstroLife`}
        breadcrumbs={[{ label: translateString("Documentation", lang), to: "/documentation" }, { label: doc.title }]}
        techArticle={{ headline: doc.title, description: doc.description, datePublished: doc.updated, dateModified: doc.updated }}
        howTo={howTo}
      />

      <div className="bg-navy pt-[72px]">
        <div className="mx-auto max-w-[90rem] px-5 lg:px-8">
          <div className="grid gap-10 py-12 lg:grid-cols-[16rem_minmax(0,1fr)_14rem]">
            {/* Left sidebar */}
            <div className="hidden lg:block"><DocsSidebar active={slug} /></div>

            {/* Content */}
            <article className="min-w-0" data-testid="doc-article">
              <nav aria-label="Breadcrumb" className="mb-4 flex items-center gap-1.5 text-sm text-silver/50">
                <Link to="/documentation" className="hover:text-white transition-colors">{translateString("Documentation", lang)}</Link>
                <span>/</span>
                <span className="text-silver/70">{doc.category}</span>
              </nav>
              <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-white">{doc.title}</h1>
              <p className="mt-3 flex items-center gap-2 text-sm text-silver/50">
                <CalendarDays className="h-4 w-4" /> {translateString("Last updated", lang)} {new Date(doc.updated).toLocaleDateString(lang === "es" ? "es-ES" : "en-GB", { year: "numeric", month: "long", day: "numeric" })}
              </p>
              <p className="mt-5 text-lg leading-relaxed text-silver/70">{doc.description}</p>

              <div className="mt-10">
                <DocContent blocks={doc.blocks} />
              </div>

              {/* Related */}
              {doc.related?.length > 0 && (
                <div className="mt-14 border-t border-navy-border pt-8">
                  <h2 className="font-heading text-lg font-bold text-white">{translateString("Related documentation", lang)}</h2>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {doc.related.filter((r) => DOCS[r]).map((r) => (
                      <Link key={r} to={`/documentation/${r}`} data-testid={`doc-related-${r}`} className="group flex items-center justify-between rounded-xl border border-navy-border bg-navy-surface p-4 hover:border-astro-blue/40 transition-colors">
                        <div><span className="text-xs text-astro-light">{translateString(DOCS[r].category, lang)}</span><p className="font-medium text-white group-hover:text-astro-light transition-colors">{translateString(DOCS[r].title, lang)}</p></div>
                        <ArrowRight className="h-4 w-4 flex-shrink-0 text-silver/40" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Prev / Next */}
              <div className="mt-12 flex items-stretch justify-between gap-4 border-t border-navy-border pt-8">
                {prev ? (
                  <Link to={`/documentation/${prev}`} data-testid="doc-prev" className="group flex flex-1 items-center gap-3 rounded-xl border border-navy-border bg-navy-surface p-4 hover:border-astro-blue/40 transition-colors">
                    <ArrowLeft className="h-4 w-4 text-silver/50" />
                    <div><span className="text-xs text-silver/40">{translateString("Previous", lang)}</span><p className="text-sm font-medium text-white group-hover:text-astro-light transition-colors">{translateString(DOCS[prev].title, lang)}</p></div>
                  </Link>
                ) : <span className="flex-1" />}
                {next ? (
                  <Link to={`/documentation/${next}`} data-testid="doc-next" className="group flex flex-1 items-center justify-end gap-3 rounded-xl border border-navy-border bg-navy-surface p-4 text-right hover:border-astro-blue/40 transition-colors">
                    <div><span className="text-xs text-silver/40">{translateString("Next", lang)}</span><p className="text-sm font-medium text-white group-hover:text-astro-light transition-colors">{translateString(DOCS[next].title, lang)}</p></div>
                    <ArrowRight className="h-4 w-4 text-silver/50" />
                  </Link>
                ) : <span className="flex-1" />}
              </div>
            </article>

            {/* On-page TOC */}
            <aside className="hidden xl:block">
              {toc.length > 0 && (
                <div className="sticky top-24">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-silver/40">{translateString("On this page", lang)}</p>
                  <ul className="space-y-2 border-l border-navy-border">
                    {toc.map((t) => (
                      <li key={t.id}>
                        <a href={`#${t.id}`} className="-ml-px block border-l-2 border-transparent pl-4 text-sm text-silver/60 hover:border-astro-blue hover:text-white transition-colors">{t.text}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        </div>
      </div>
    </>
  );
}
